'use client'

import { ensureGasBalance } from '@/lib/solana/ensureGasBalance'
import { useWallet } from '@/providers/WalletContext'
import { useSolanaTransaction } from '@account-kit/react'
import { VersionedTransaction } from '@solana/web3.js'
// Other imports commented out with the unused functions:
// AddressLookupTableAccount, Connection, PublicKey, SystemProgram, TransactionInstruction, TransactionMessage
import { useMutation, useQueryClient } from '@tanstack/react-query'

// Solana USDC mint address
export const SOLANA_USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'

/*
// Get Alchemy's sponsor address by making a dummy request (unused - kept for future use)
async function getAlchemySponsorAddress(
	connection: Connection,
	userAddress: string,
	policyId: string
): Promise<string> {
	// Create a minimal dummy transaction to discover the sponsor address
	const { blockhash } = await connection.getLatestBlockhash({ commitment: 'finalized' })

	const dummyInstruction = SystemProgram.transfer({
		fromPubkey: new PublicKey(userAddress),
		toPubkey: new PublicKey(userAddress),
		lamports: 0,
	})

	const message = new TransactionMessage({
		payerKey: new PublicKey(userAddress),
		recentBlockhash: blockhash,
		instructions: [dummyInstruction],
	}).compileToV0Message()

	const dummyTx = new VersionedTransaction(message)
	const serializedTransaction = Buffer.from(dummyTx.serialize()).toString('base64')

	const body = JSON.stringify({
		id: Math.floor(Math.random() * 1000000),
		jsonrpc: '2.0',
		method: 'alchemy_requestFeePayer',
		params: [{ policyId, serializedTransaction }],
	})

	const response = await fetch(connection.rpcEndpoint, {
		method: 'POST',
		headers: { accept: 'application/json', 'content-type': 'application/json' },
		body,
	})

	const jsonResponse = await response.json()
	if (!jsonResponse?.result?.serializedTransaction) {
		throw new Error(`Failed to get sponsor: ${JSON.stringify(jsonResponse)}`)
	}

	// Deserialize the modified transaction to find the sponsor (new fee payer)
	const modifiedTx = VersionedTransaction.deserialize(
		Buffer.from(jsonResponse.result.serializedTransaction, 'base64')
	)

	// The fee payer is the first account in the static account keys
	const sponsorAddress = modifiedTx.message.staticAccountKeys[0].toBase58()
	console.log('[DflowSwap] Discovered Alchemy sponsor address:', sponsorAddress)

	return sponsorAddress
}
*/

/*
// Extract instructions from a VersionedTransaction (unused - kept for future use)
async function extractInstructions(
	transaction: VersionedTransaction,
	connection: Connection
): Promise<TransactionInstruction[]> {
	const message = transaction.message

	// Get address lookup tables if any
	const addressTableLookups = message.addressTableLookups
	let lookupTableAccounts: AddressLookupTableAccount[] = []

	if (addressTableLookups.length > 0) {
		const lookupTableAddresses = addressTableLookups.map(lookup => lookup.accountKey)
		const lookupTableAccountInfos = await connection.getMultipleAccountsInfo(lookupTableAddresses)

		lookupTableAccounts = lookupTableAccountInfos
			.map((info, index) => {
				if (!info) return null
				return new AddressLookupTableAccount({
					key: lookupTableAddresses[index],
					state: AddressLookupTableAccount.deserialize(info.data),
				})
			})
			.filter((account): account is AddressLookupTableAccount => account !== null)
	}

	// Get all account keys (static + from lookup tables)
	const accountKeys = message.getAccountKeys({ addressLookupTableAccounts: lookupTableAccounts })

	// Convert compiled instructions to TransactionInstruction
	const instructions: TransactionInstruction[] = message.compiledInstructions.map(compiledIx => {
		const programId = accountKeys.get(compiledIx.programIdIndex)
		if (!programId) throw new Error('Program ID not found')

		const keys = compiledIx.accountKeyIndexes.map(index => {
			const pubkey = accountKeys.get(index)
			if (!pubkey) throw new Error('Account key not found')

			return {
				pubkey,
				isSigner: message.isAccountSigner(index),
				isWritable: message.isAccountWritable(index),
			}
		})

		return new TransactionInstruction({
			programId,
			keys,
			data: Buffer.from(compiledIx.data),
		})
	})

	return instructions
}
*/

type DflowOrderResponse = {
	transaction: string // Base64 encoded VersionedTransaction
	executionMode: 'sync' | 'async'
	quote: {
		inAmount: string
		outAmount: string
		priceImpactPct: number
	}
}

type SwapParams = {
	inputMint: string
	outputMint: string
	amount: string // Amount in smallest units (e.g., USDC has 6 decimals)
	slippageBps?: number
}

async function fetchDflowOrder(
	params: SwapParams & { userPublicKey: string; sponsor?: string }
): Promise<DflowOrderResponse> {
	const searchParams = new URLSearchParams({
		inputMint: params.inputMint,
		outputMint: params.outputMint,
		amount: params.amount,
		slippageBps: (params.slippageBps ?? 50).toString(),
		userPublicKey: params.userPublicKey,
	})

	// Add sponsor for gasless swaps
	if (params.sponsor) {
		searchParams.set('sponsor', params.sponsor)
	}

	const response = await fetch(`/api/dflow/order?${searchParams.toString()}`)

	if (!response.ok) {
		const error = await response.json()
		throw new Error(error.details || error.error || 'Failed to get order')
	}

	return response.json()
}

/*
// Sign and broadcast a sponsored transaction (unused - kept for future use)
async function signAndBroadcastSponsoredTransaction(
	transaction: VersionedTransaction,
	connection: Connection,
	policyId: string,
	userAddress: string,
	signer: { addSignature: (tx: VersionedTransaction) => Promise<VersionedTransaction> }
): Promise<string> {
	console.log(
		'[DflowSwap] Original tx accounts:',
		transaction.message.staticAccountKeys.map(k => k.toBase58())
	)
	console.log(
		'[DflowSwap] Original tx signatures required:',
		transaction.message.header.numRequiredSignatures
	)

	// 1. Send to Alchemy to get sponsor signature
	const serializedTransaction = Buffer.from(transaction.serialize()).toString('base64')

	const body = JSON.stringify({
		id: Math.floor(Math.random() * 1000000),
		jsonrpc: '2.0',
		method: 'alchemy_requestFeePayer',
		params: [{ policyId, serializedTransaction }],
	})

	const response = await fetch(connection.rpcEndpoint, {
		method: 'POST',
		headers: { accept: 'application/json', 'content-type': 'application/json' },
		body,
	})

	const jsonResponse = await response.json()
	if (!jsonResponse?.result?.serializedTransaction) {
		throw new Error(`Failed to get sponsor signature: ${JSON.stringify(jsonResponse)}`)
	}

	// 2. Get the transaction with sponsor's signature
	const sponsoredTx = VersionedTransaction.deserialize(
		Buffer.from(jsonResponse.result.serializedTransaction, 'base64')
	)

	const sponsoredAccounts = sponsoredTx.message.staticAccountKeys.map(k => k.toBase58())
	console.log('[DflowSwap] After Alchemy - accounts:', sponsoredAccounts)
	console.log(
		'[DflowSwap] After Alchemy - signatures required:',
		sponsoredTx.message.header.numRequiredSignatures
	)
	console.log('[DflowSwap] After Alchemy - signatures present:', sponsoredTx.signatures.length)

	// Check which signatures are filled
	sponsoredTx.signatures.forEach((sig, i) => {
		const isEmpty = sig.every(b => b === 0)
		const account = sponsoredAccounts[i] || 'unknown'
		console.log(`[DflowSwap] Signature ${i} (${account}): ${isEmpty ? 'EMPTY' : 'FILLED'}`)
	})

	// Find user's position in accounts
	const userIndex = sponsoredAccounts.findIndex(a => a === userAddress)
	console.log('[DflowSwap] User address:', userAddress)
	console.log('[DflowSwap] User index in accounts:', userIndex)

	console.log('[DflowSwap] Adding user signature...')

	// 3. Add user's signature
	await signer.addSignature(sponsoredTx)

	// Check signatures after user signs
	sponsoredTx.signatures.forEach((sig, i) => {
		const isEmpty = sig.every(b => b === 0)
		const account = sponsoredAccounts[i] || 'unknown'
		console.log(
			`[DflowSwap] After user sig - Signature ${i} (${account}): ${isEmpty ? 'EMPTY' : 'FILLED'}`
		)
	})

	console.log('[DflowSwap] Broadcasting transaction...')

	// 4. Broadcast
	const signature = await connection.sendRawTransaction(sponsoredTx.serialize(), {
		skipPreflight: false,
		preflightCommitment: 'confirmed',
	})

	// 5. Confirm
	await connection.confirmTransaction(signature, 'confirmed')

	return signature
}
*/

export function useDflowSwap() {
	const { solanaAddress, isSolanaReady } = useWallet()
	// NOTE: Alchemy sponsorship temporarily disabled - user pays gas
	// const policyId = process.env.NEXT_PUBLIC_ALCHEMY_POLICY_ID
	const { connection, signer } = useSolanaTransaction()
	const queryClient = useQueryClient()

	const mutation = useMutation({
		mutationFn: async (params: SwapParams) => {
			if (!solanaAddress) {
				throw new Error('Solana wallet not connected')
			}
			if (!connection) {
				throw new Error('Solana connection not available')
			}
			if (!signer) {
				throw new Error('Signer not available')
			}

			// Ensure user has enough SOL for gas (auto-faucet if needed)
			await ensureGasBalance(connection, solanaAddress)
			const orderResponse = await fetchDflowOrder({
				...params,
				userPublicKey: solanaAddress,
				// No sponsor - user pays gas
			})

			// Deserialize the dflow transaction
			const transactionBuffer = Buffer.from(orderResponse.transaction, 'base64')
			const transaction = VersionedTransaction.deserialize(transactionBuffer)
			const signedTx = await signer.addSignature(transaction)
			let signature: string
			try {
				signature = await connection.sendRawTransaction(signedTx.serialize(), {
					skipPreflight: false,
					preflightCommitment: 'confirmed',
				})
			} catch (err) {
				// Check for the specific error when faucet funds haven't landed yet
				const errorMessage = err instanceof Error ? err.message : String(err)
				if (
					errorMessage.includes('Attempt to debit an account but found no record of a prior credit')
				) {
					throw new Error(
						"We're having issues landing the transaction on Solana. The chain might be busy, please retry."
					)
				}
				throw err
			}
			for (let i = 0; i < 30; i++) {
				const status = await connection.getSignatureStatus(signature)
				if (
					status.value?.confirmationStatus === 'confirmed' ||
					status.value?.confirmationStatus === 'finalized'
				) {
					break
				}
				if (status.value?.err) {
					throw new Error(`Transaction failed: ${JSON.stringify(status.value.err)}`)
				}
				await new Promise(resolve => setTimeout(resolve, 1000))
			}

			return {
				signature,
				quote: orderResponse.quote,
				executionMode: orderResponse.executionMode,
			}
		},
		onSuccess: () => {
			// Invalidate balance queries
			queryClient.invalidateQueries({ queryKey: ['solanaBalance'] })
			queryClient.invalidateQueries({ queryKey: ['kalshiPositions'] })
		},
	})

	return {
		swap: mutation.mutate,
		swapAsync: mutation.mutateAsync,
		isPending: mutation.isPending,
		isSuccess: mutation.isSuccess,
		isError: mutation.isError,
		error: mutation.error,
		data: mutation.data,
		reset: mutation.reset,
		isReady: isSolanaReady,
	}
}

// Convenience hooks for buying/selling
export function useBuyOutcome() {
	const { swapAsync, ...rest } = useDflowSwap()

	const buy = async (params: {
		outcomeMint: string // YES or NO token mint
		usdcAmount: string // Amount in USDC (6 decimals)
		slippageBps?: number
	}) => {
		return swapAsync({
			inputMint: SOLANA_USDC_MINT,
			outputMint: params.outcomeMint,
			amount: params.usdcAmount,
			slippageBps: params.slippageBps,
		})
	}

	return { buy, ...rest }
}

export function useSellOutcome() {
	const { swapAsync, ...rest } = useDflowSwap()

	const sell = async (params: {
		outcomeMint: string // YES or NO token mint
		tokenAmount: string // Amount of outcome tokens to sell
		slippageBps?: number
	}) => {
		return swapAsync({
			inputMint: params.outcomeMint,
			outputMint: SOLANA_USDC_MINT,
			amount: params.tokenAmount,
			slippageBps: params.slippageBps,
		})
	}

	return { sell, ...rest }
}
